/**
 * Mixing graph:
 *
 *   sfx voices ──► sfx.dry ─────────────► fader ─┐
 *              └─► sfx.wet ─────────────► fader ─┼─► reverb in ─► convolver ─► return ─┐
 *   ambience ──► amb.dry/wet ─► lowpass ─► fader ─┘   (amb feeds the sfx bus)           │
 *   music ─────► music.dry/wet ─► lowpass ─► fader ──────────────────────────────────── ┤
 *                                                                                       ▼
 *                                   master ─► highpass 28 Hz ─► compressor ─► mute ─► destination
 *
 * Each bus has matched dry + wet faders so the user's volume and state ducking also scale the
 * reverb that bus produces.
 */
import { Smooth, disconnect, type Later } from './util';
import type { Route } from './voice';

/** A dry/wet pair of gains that crossfade together (theme layers). */
export class FadeRoute implements Route {
  readonly dry: GainNode;
  readonly wet: GainNode;
  private from: number;
  private to: number;
  private t0 = 0;
  private t1 = 0;

  constructor(ctx: BaseAudioContext, dest: Route, initial = 0) {
    this.dry = ctx.createGain();
    this.wet = ctx.createGain();
    this.dry.gain.value = initial;
    this.wet.gain.value = initial;
    this.from = initial;
    this.to = initial;
    this.dry.connect(dest.dry);
    this.wet.connect(dest.wet);
  }

  /** Current (scheduled) level, computed analytically — no reliance on AudioParam.value. */
  level(now: number): number {
    if (now >= this.t1) return this.to;
    if (now <= this.t0) return this.from;
    return this.from + ((this.to - this.from) * (now - this.t0)) / (this.t1 - this.t0);
  }

  fade(to: number, now: number, seconds: number): number {
    const cur = this.level(now);
    const end = now + Math.max(0.01, seconds);
    for (const g of [this.dry.gain, this.wet.gain]) {
      g.cancelScheduledValues(now);
      g.setValueAtTime(cur, now);
      g.linearRampToValueAtTime(to, end);
    }
    this.from = cur;
    this.to = to;
    this.t0 = now;
    this.t1 = end;
    return end;
  }

  dispose(): void {
    disconnect(this.dry);
    disconnect(this.wet);
  }
}

export interface BusOptions {
  lowpass?: boolean;
}

export class Bus implements Route {
  readonly dry: GainNode;
  readonly wet: GainNode;
  private readonly faderS: Smooth[];
  private readonly lpS: Smooth[] = [];
  private readonly nodes: AudioNode[] = [];
  private readonly maxHz: number;
  private volume = 1;
  private duck = 1;

  constructor(ctx: BaseAudioContext, dest: Route, opts: BusOptions = {}) {
    this.maxHz = Math.min(20000, ctx.sampleRate * 0.45);
    this.dry = ctx.createGain();
    this.wet = ctx.createGain();
    const fDry = ctx.createGain();
    const fWet = ctx.createGain();
    this.faderS = [new Smooth(fDry.gain, 1e-4, 1), new Smooth(fWet.gain, 1e-4, 1)];
    this.nodes.push(this.dry, this.wet, fDry, fWet);
    const pairs: [GainNode, GainNode][] = [
      [this.dry, fDry],
      [this.wet, fWet],
    ];
    for (const [input, fader] of pairs) {
      if (opts.lowpass) {
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.Q.value = 0.5;
        lp.frequency.value = this.maxHz;
        this.lpS.push(new Smooth(lp.frequency, 1, this.maxHz));
        this.nodes.push(lp);
        input.connect(lp);
        lp.connect(fader);
      } else {
        input.connect(fader);
      }
    }
    fDry.connect(dest.dry);
    fWet.connect(dest.wet);
  }

  setVolume(v: number, now: number, tau = 0.05): void {
    this.volume = v;
    this.apply(now, tau);
  }

  setDuck(d: number, now: number, tau: number): void {
    this.duck = d;
    this.apply(now, tau);
  }

  setLowpass(hz: number, now: number, tau: number): void {
    const f = Math.max(80, Math.min(hz, this.maxHz));
    for (const s of this.lpS) s.to(f, now, tau);
  }

  /** Open the low-pass fully. */
  openLowpass(now: number, tau: number): void {
    this.setLowpass(this.maxHz, now, tau);
  }

  private apply(now: number, tau: number): void {
    const g = this.volume * this.duck;
    for (const s of this.faderS) s.to(g, now, tau);
  }

  dispose(): void {
    for (const n of this.nodes) disconnect(n);
  }
}

interface ReverbSlot {
  readonly input: GainNode;
  readonly conv: ConvolverNode;
  readonly ret: GainNode;
  readonly level: number;
  /** Fade-in window (return ramps 0 → level linearly). */
  readonly t0: number;
  readonly t1: number;
}

export class Mixer {
  readonly sfx: Bus;
  readonly music: Bus;
  readonly amb: Bus;
  private readonly master: GainNode;
  private readonly hp: BiquadFilterNode;
  private readonly comp: DynamicsCompressorNode;
  private readonly mute: GainNode;
  private readonly reverbIn: GainNode;
  private readonly masterS: Smooth;
  private readonly muteS: Smooth;
  private reverb: ReverbSlot | null = null;

  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly later: Later,
    destination: AudioNode,
  ) {
    this.master = ctx.createGain();
    this.masterS = new Smooth(this.master.gain, 1e-4, 0.9);
    this.hp = ctx.createBiquadFilter();
    this.hp.type = 'highpass';
    this.hp.frequency.value = 28;
    this.hp.Q.value = 0.7;
    this.comp = ctx.createDynamicsCompressor();
    // Gentle glue: catches pile-ups (crash + music + wind) without pumping.
    this.comp.threshold.value = -15;
    this.comp.knee.value = 9;
    this.comp.ratio.value = 4;
    this.comp.attack.value = 0.004;
    this.comp.release.value = 0.22;
    this.mute = ctx.createGain();
    this.muteS = new Smooth(this.mute.gain, 1e-4, 1);
    this.master.connect(this.hp);
    this.hp.connect(this.comp);
    this.comp.connect(this.mute);
    this.mute.connect(destination);

    this.reverbIn = ctx.createGain();
    const root: Route = { dry: this.master, wet: this.reverbIn };
    this.sfx = new Bus(ctx, root);
    this.music = new Bus(ctx, root, { lowpass: true });
    this.amb = new Bus(ctx, this.sfx, { lowpass: true });
  }

  setMaster(v: number, now: number): void {
    this.masterS.to(v, now, 0.05);
  }

  setMuted(m: boolean, now: number): void {
    this.muteS.to(m ? 0 : 1, now, 0.04);
  }

  /** Fast click-free fade to silence (before teardown). */
  silence(now: number): void {
    this.muteS.to(0, now, 0.012);
  }

  /** Crossfade to a new impulse response (a fresh ConvolverNode; never re-assigns a buffer). */
  setReverb(buffer: AudioBuffer, level: number, now: number, fade: number): void {
    const ctx = this.ctx;
    const input = ctx.createGain();
    const conv = ctx.createConvolver();
    conv.normalize = true;
    conv.buffer = buffer;
    const ret = ctx.createGain();
    ret.gain.value = 0;
    this.reverbIn.connect(input);
    input.connect(conv);
    conv.connect(ret);
    ret.connect(this.master);
    const f = Math.max(0.05, fade);
    ret.gain.setValueAtTime(0, now);
    ret.gain.linearRampToValueAtTime(level, now + f);

    const old = this.reverb;
    this.reverb = { input, conv, ret, level, t0: now, t1: now + f };
    if (old) {
      const cur = now >= old.t1 ? old.level : (old.level * Math.max(0, now - old.t0)) / (old.t1 - old.t0);
      old.ret.gain.cancelScheduledValues(now);
      old.ret.gain.setValueAtTime(cur, now);
      old.ret.gain.linearRampToValueAtTime(0, now + f);
      this.later.add(now + f + 0.1, () => {
        try {
          this.reverbIn.disconnect(old.input);
        } catch {
          /* selective disconnect unsupported: input is orphaned but outputs nothing */
        }
        disconnect(old.input);
        disconnect(old.conv);
        disconnect(old.ret);
      });
    }
  }

  dispose(): void {
    this.sfx.dispose();
    this.music.dispose();
    this.amb.dispose();
    if (this.reverb) {
      disconnect(this.reverb.input);
      disconnect(this.reverb.conv);
      disconnect(this.reverb.ret);
      this.reverb = null;
    }
    for (const n of [this.reverbIn, this.master, this.hp, this.comp, this.mute]) disconnect(n);
  }
}
